export const calculatePerformanceStats = (trades) => {
  if (!trades || trades.length === 0) {
    return {
      totalTrades: 0,
      winningTrades: 0,
      losingTrades: 0,
      winRate: 0,
      profitFactor: 0,
      averageWin: 0,
      averageLoss: 0,
      netProfit: 0,
      bestTrade: 0,
      worstTrade: 0,
      maxDrawdown: 0,
      maxDrawdownPct: 0,
      averageR: 0,
      averageDurationMs: 0,
      averageDurationReadable: '0m',
      // Advanced metrics
      sharpeRatio: 0,
      sortinoRatio: 0,
      calmarRatio: 0,
      expectancy: 0,
      payoffRatio: 0,
      totalReturn: 0,
      totalReturnPct: 0,
      maxConsecutiveWins: 0,
      maxConsecutiveLosses: 0,
      longTrades: 0,
      shortTrades: 0,
      longWinRate: 0,
      shortWinRate: 0,
      averageTradePnl: 0,
    };
  }

  let winningTrades = 0;
  let losingTrades = 0;
  let totalProfit = 0;
  let totalLoss = 0;
  let netProfit = 0;
  let bestTrade = -Infinity;
  let worstTrade = Infinity;
  let cumulative = 0;
  let maxDrawdown = 0; // measured on cumulative P&L path
  let peak = 0;
  let totalDurationMs = 0;
  let rSum = 0;
  let rCount = 0;

  // For Sharpe/Sortino calculations
  const returns = [];
  let previousEquity = 0;

  // For consecutive win/loss tracking
  let currentWinStreak = 0;
  let currentLossStreak = 0;
  let maxConsecutiveWins = 0;
  let maxConsecutiveLosses = 0;

  // For long/short breakdown
  let longTrades = 0;
  let shortTrades = 0;
  let longWins = 0;
  let shortWins = 0;

  // Build equity curve for calculations
  const equityCurve = [];

  // Sort trades by date for proper equity curve calculation
  const sortedTrades = [...trades].sort((a, b) =>
    new Date(a.created_at || a.closed_at || 0).getTime() - new Date(b.created_at || b.closed_at || 0).getTime()
  );

  sortedTrades.forEach(trade => {
    const pnl = parseFloat(trade.actual_profit_loss);
    if (isNaN(pnl)) return;

    if (pnl > 0) {
      winningTrades++;
      totalProfit += pnl;
      currentWinStreak++;
      currentLossStreak = 0;
      maxConsecutiveWins = Math.max(maxConsecutiveWins, currentWinStreak);
    } else if (pnl < 0) {
      losingTrades++;
      totalLoss += Math.abs(pnl);
      currentLossStreak++;
      currentWinStreak = 0;
      maxConsecutiveLosses = Math.max(maxConsecutiveLosses, currentLossStreak);
    } else {
      // Breakeven trade resets streaks
      currentWinStreak = 0;
      currentLossStreak = 0;
    }

    // Long/Short breakdown
    const direction = (trade.direction || '').toUpperCase();
    if (direction === 'BUY' || direction === 'LONG') {
      longTrades++;
      if (pnl > 0) longWins++;
    } else if (direction === 'SELL' || direction === 'SHORT') {
      shortTrades++;
      if (pnl > 0) shortWins++;
    }

    netProfit += pnl;
    if (pnl > bestTrade) bestTrade = pnl;
    if (pnl < worstTrade) worstTrade = pnl;

    // equity curve stats using cumulative P&L
    cumulative += pnl;
    equityCurve.push(cumulative);
    if (cumulative > peak) peak = cumulative;
    const dd = peak - cumulative; // drawdown depth
    if (dd > maxDrawdown) maxDrawdown = dd;

    // Calculate period return for Sharpe/Sortino
    if (previousEquity !== 0) {
      const periodReturn = (cumulative - previousEquity) / Math.abs(previousEquity);
      returns.push(periodReturn);
    } else if (cumulative !== 0) {
      // First trade return is based on initial balance assumption
      returns.push(pnl > 0 ? pnl / Math.abs(pnl) : pnl / Math.abs(pnl || 1));
    }
    previousEquity = cumulative;

    // Average duration (if timestamps exist)
    const openTs = trade.created_at ? new Date(trade.created_at).getTime() : null;
    const closeTs = trade.closed_at ? new Date(trade.closed_at).getTime() : null;
    if (Number.isFinite(openTs) && Number.isFinite(closeTs) && closeTs >= openTs) {
      totalDurationMs += (closeTs - openTs);
    }

    // Average R if risk per trade provided
    // Support fields: trade.r_multiple OR trade.risk_amount and pnl; fallback to null
    if (trade.r_multiple != null && !Number.isNaN(Number(trade.r_multiple))) {
      rSum += Number(trade.r_multiple);
      rCount += 1;
    } else if (trade.risk_amount != null && !Number.isNaN(Number(trade.risk_amount)) && Number(trade.risk_amount) !== 0) {
      rSum += pnl / Number(trade.risk_amount);
      rCount += 1;
    }
  });

  const totalTrades = winningTrades + losingTrades;
  const winRate = totalTrades > 0 ? (winningTrades / totalTrades) * 100 : 0;
  const profitFactor = totalLoss > 0 ? totalProfit / totalLoss : 0;
  const averageWin = winningTrades > 0 ? totalProfit / winningTrades : 0;
  const averageLoss = losingTrades > 0 ? totalLoss / losingTrades : 0;
  const averageR = rCount > 0 ? rSum / rCount : 0;
  const averageDurationMs = totalTrades > 0 ? totalDurationMs / totalTrades : 0;

  // Calculate Max Drawdown Percentage (relative to peak equity)
  // Note: Without account balance, this uses peak cumulative P&L as denominator.
  // Backend stats with account balance are preferred for accurate DD%.
  const maxDrawdownPct = peak > 0 ? (maxDrawdown / peak) * 100 : 0;

  // Calculate Sharpe Ratio (annualized, assuming ~252 trading days)
  let sharpeRatio = 0;
  if (returns.length > 1) {
    const meanReturn = returns.reduce((a, b) => a + b, 0) / returns.length;
    const variance = returns.reduce((sum, r) => sum + Math.pow(r - meanReturn, 2), 0) / returns.length;
    const stdDev = Math.sqrt(variance);
    if (stdDev > 0) {
      // Annualize: assuming trades represent daily activity, scale by sqrt(252)
      sharpeRatio = (meanReturn / stdDev) * Math.sqrt(252);
    }
  }

  // Calculate Sortino Ratio (only considers downside deviation)
  let sortinoRatio = 0;
  if (returns.length > 1) {
    const meanReturn = returns.reduce((a, b) => a + b, 0) / returns.length;
    const downsideReturns = returns.filter(r => r < 0);
    if (downsideReturns.length > 0) {
      const downsideVariance = downsideReturns.reduce((sum, r) => sum + Math.pow(r, 2), 0) / downsideReturns.length;
      const downsideDev = Math.sqrt(downsideVariance);
      if (downsideDev > 0) {
        sortinoRatio = (meanReturn / downsideDev) * Math.sqrt(252);
      }
    }
  }

  // Calculate Calmar Ratio (annualized return / max drawdown)
  let calmarRatio = 0;
  const totalReturn = netProfit;
  const totalReturnPct = peak > 0 ? (netProfit / peak) * 100 : 0;
  if (maxDrawdownPct > 0) {
    // Annualize return based on trading period (simplified)
    calmarRatio = totalReturnPct / maxDrawdownPct;
  }

  // Expectancy = (Win Rate * Average Win) - (Loss Rate * Average Loss)
  const lossRate = totalTrades > 0 ? (losingTrades / totalTrades) : 0;
  const expectancy = ((winRate / 100) * averageWin) - (lossRate * averageLoss);

  // Payoff Ratio (Reward/Risk Ratio) = Average Win / Average Loss
  const payoffRatio = averageLoss > 0 ? averageWin / averageLoss : 0;

  // Long/Short win rates
  const longWinRate = longTrades > 0 ? (longWins / longTrades) * 100 : 0;
  const shortWinRate = shortTrades > 0 ? (shortWins / shortTrades) * 100 : 0;

  // Average trade P&L
  const averageTradePnl = totalTrades > 0 ? netProfit / totalTrades : 0;

  const mins = Math.floor(averageDurationMs / 60000);
  const hours = Math.floor(mins / 60);
  const days = Math.floor(hours / 24);
  const remHours = hours % 24;
  const remMins = mins % 60;
  const averageDurationReadable = days > 0
    ? `${days}d ${remHours}h`
    : hours > 0
      ? `${hours}h ${remMins}m`
      : `${remMins}m`;

  return {
    totalTrades,
    winningTrades,
    losingTrades,
    winRate,
    profitFactor,
    averageWin,
    averageLoss,
    netProfit,
    bestTrade: Number.isFinite(bestTrade) ? bestTrade : 0,
    worstTrade: Number.isFinite(worstTrade) ? worstTrade : 0,
    maxDrawdown,
    maxDrawdownPct,
    averageR,
    averageDurationMs,
    averageDurationReadable,
    // Advanced metrics
    sharpeRatio,
    sortinoRatio,
    calmarRatio,
    expectancy,
    payoffRatio,
    totalReturn,
    totalReturnPct,
    maxConsecutiveWins,
    maxConsecutiveLosses,
    longTrades,
    shortTrades,
    longWinRate,
    shortWinRate,
    averageTradePnl,
  };
};
